
import { defineComponent, reactive, toRefs, ref, onMounted, onActivated } from "vue";
import { useI18n } from "vue-i18n";
import { useUserInfoStore } from "@/stores/UserInfoStore";
import API from "@/apis/TPSAPI";
import { APIResultCode } from "@/models/enums/APIResultCode";
import _ from "lodash";
import { usePaginatorSetup } from "@/stores/PaginatorStore";
import { ElMessage, ElMessageBox } from "element-plus";
import IReqTopPNInfoDTO from "@/models/dto/IReqTopPNInfoDTO";
import IReqTopPNInfoSearchDTO from "@/models/dto/IReqTopPNInfoSearchDTO";
import IResTopPNInfoDetailDTO from "@/models/dto/IResTopPNInfoDetailDTO";
import IResTopPNInfoTableViewModelDTO from "@/models/dto/IResTopPNInfoTableViewModelDTO";
import ElTable from "element-plus/lib/components/table";
import { delay } from "@/plugins/utility";
import { DataTableType } from "@/models/enums/DataTableType";
import numeral from "numeral";

export default defineComponent({
  props: {
    pjcode: String,
  },
  setup(props) {
    const { t } = useI18n();
    const userInfoStore = useUserInfoStore();
    const paginatorSetup = usePaginatorSetup();
    const tableType = DataTableType.topPNInfoTable;

    const state = reactive({
      isTableLoading: false,
      dialogTopVh: "1vh",
      formLabelPosition: "right" as "top" | "left" | "right",
      formItemSize: "default" as "default" | "small",
      formLabelWidth: "180px" as string,

      // 成品料號改善清單
      displayTopPNInfoList: [] as IResTopPNInfoTableViewModelDTO[],

      //搜尋改善事項參數
      searchCriteria: {
        page: 1,
        pageSize: 20,
      } as IReqTopPNInfoSearchDTO,

      isShowModifyDialog: false,
      isModifyDialogLoading: false,
      isShowViewDialog: false,
      selectedTopPNInfo: {} as IResTopPNInfoDetailDTO,

      filteredTotal: 0,
      colDisplayCtrl: new Array(50).fill(true) as boolean[],
    });

    //#region 建立表單ref與Validator
    const TopPNInfoTableRef = ref<InstanceType<typeof ElTable>>();

    const ufloatValidator = async (source: any, value: any, callback: any) => {
      const filed = source.field as "areaBeforeImp" | "pphBeforeImp" | "areaAfterImp" | "pphAfterImp";
      console.log(`validate(ufloat) : ${source.field}`);

      if (!value) {
        state.selectedTopPNInfo[filed] = undefined;
        return;
      }

      let InputNum = Number(value);
      if (Number.isNaN(InputNum)) {
        console.log("not a number");
        callback(new Error(t("validation_msg.invalid_ufloat_value")));
        return;
      }

      if (InputNum <= 0) {
        console.log("not a number");
        callback(new Error(t("validation_msg.invalid_ufloat_value")));
        return;
      }

      state.selectedTopPNInfo[filed] = InputNum;
      callback();
    };

    const uintValidator = async (source: any, value: any, callback: any) => {
      const filed = source.field as "procLTBeforeImp" | "venderLTBeforeImp" | "procLTAfterImp" | "venderLTAfterImp";
      console.log(`validate(uint) : ${source.field}`);

      if (!value) {
        state.selectedTopPNInfo[filed] = undefined;
        return;
      }

      let InputNum = Number(value);
      if (Number.isNaN(InputNum)) {
        callback(new Error(t("validation_msg.invalid_uint_value")));
        return;
      }

      if (InputNum <= 0) {
        console.log("not a number");
        callback(new Error(t("validation_msg.invalid_uint_value")));
        return;
      }

      state.selectedTopPNInfo[filed] = Math.round(InputNum);
      callback();
    };

    const modifyTopPNInfoFormRef = ref();
    const modifyTopPNInfoFormRules = ref({
      areaBeforeImp: [{ required: false, validator: ufloatValidator, trigger: "blur" }],
      procLTBeforeImp: [{ required: false, validator: uintValidator, trigger: "blur" }],
      venderLTBeforeImp: [{ required: false, validator: uintValidator, trigger: "blur" }],
      pphBeforeImp: [{ required: false, validator: ufloatValidator, trigger: "blur" }],

      areaAfterImp: [{ required: false, validator: ufloatValidator, trigger: "blur" }],
      procLTAfterImp: [{ required: false, validator: uintValidator, trigger: "blur" }],
      venderLTAfterImp: [{ required: false, validator: uintValidator, trigger: "blur" }],
      pphAfterImp: [{ required: false, validator: ufloatValidator, trigger: "blur" }],
    });

    const periodNumSearchValidator = async (source: any, value: any, callback: any) => {
      const filed = source.field as "periodNum";
      console.log(`validate(uint) : ${source.field}`);
      console.log(`source : ${source}`);
      if (!value) {
        state.searchCriteria[filed] = undefined;
        return;
      }

      var inputNum = Number(value);
      if (Number.isNaN(inputNum)) {
        callback(new Error(t("validation_msg.invalid_uint_value")));
        return;
      }

      if (inputNum <= 0) {
        state.searchCriteria[filed] = 1;
        return;
      }

      state.searchCriteria[filed] = Math.round(inputNum);
      callback();
    };
    const searchCriteriaFormRef = ref();
    const searchCriteriaFormRules = ref({
      periodNum: [{ validator: periodNumSearchValidator, trigger: "blur" }],
    });
    //#endregion

    //#region Hook functions
    onMounted(async () => {
      //更新使用者設置偏好
      await updateUserPreference();

      //取得成品料號改善清單
      await searchTopPNInfo();
    });
    //#endregion

    //#region UI Events
    /** 按下清除按鈕 */
    const onClearSearchTopPNInfoClicked = () => {
      state.searchCriteria.periodNum = undefined;
      state.searchCriteria.topPNKeyword = undefined;

      //清除table排序
      TopPNInfoTableRef.value?.clearSort();
    };

    /** 按下搜尋按鈕 */
    const onSearchTopPNInfoClicked = async () => {
      try {
        /* 檢查欄位 */
        await searchCriteriaFormRef.value.validate();

        //清除table排序
        TopPNInfoTableRef.value?.clearSort();

        /* 取得成品料號改善清單 */
        await searchTopPNInfo();
      } catch (error) {
        console.error(error);
      }
    };

    /** 按下並放開Enter鍵 */
    const onInputFieldEnterKeyUp = async () => {
      console.log("onInputFieldEnterKeyUp");

      /* 取得成品料號改善清單 */
      await searchTopPNInfo();
    };

    /** 改變排序 */
    const onSortChanged = (sort: any) => {
      console.log(`onSortChanged: ${sort.column}, ${sort.prop}, ${sort.order}`);
      state.searchCriteria.sortColName = sort.prop;
      state.searchCriteria.sortOrder = sort.order;
      searchTopPNInfo();
      console.log("onCurrentPageChanged > scroll to 0,0");
      window.scrollTo(0, 0);
    };

    /** 分頁輸入 */
    const onPagesizeChanged = async () => {
      /* 取得成品料號改善清單 */
      await searchTopPNInfo();
      window.scrollTo(0, 0);
    };

    const onCurrentPageChanged = async () => {
      /* 取得成品料號改善清單 */
      await searchTopPNInfo();
      window.scrollTo(0, 0);
    };

    /** 關閉欄位顯示設定Panel時 */
    const onColDisplayCtrlPanelHidded = async () => {
      await API.patchTableDisplayPreference(tableType, { displayMap: state.colDisplayCtrl });
    };

    /** 按下編輯按鈕 */
    const onEditTopPNInfoClicked = async (target: IResTopPNInfoTableViewModelDTO) => {
      try {
        state.isTableLoading = true;

        await GetSelectedItemDetailAsync(target.id);

        setFormStyle();

        state.isShowModifyDialog = true;
      } catch (error) {
        console.log(error);
        ElMessage({
          type: "error",
          message: "處理失敗，請查看詳細訊息",
        });
      } finally {
        state.isTableLoading = false;
      }
    };

    /**  編輯 送出 */
    const onSaveModifyResultClicked = async () => {
      try {
        await modifyTopPNInfoFormRef.value?.validate();
      } catch (error) {
        console.log(error);
        return;
      }

      try {
        await ElMessageBox.confirm(t("update_confirm_msg"), t("Warning"), {
          confirmButtonText: t("Confirm"),
          cancelButtonText: t("Cancel"),
          type: "warning",
        });
      } catch (error) {
        return;
      }

      try {
        state.isModifyDialogLoading = true;

        const newTopPNInfo: IReqTopPNInfoDTO = {
          topPN: state.selectedTopPNInfo.topPN,
          periodNum: state.selectedTopPNInfo.periodNum,
          areaBeforeImp: state.selectedTopPNInfo.areaBeforeImp,
          procLTBeforeImp: state.selectedTopPNInfo.procLTBeforeImp,
          venderLTBeforeImp: state.selectedTopPNInfo.venderLTBeforeImp,
          pphBeforeImp: state.selectedTopPNInfo.pphBeforeImp,
          areaAfterImp: state.selectedTopPNInfo.areaAfterImp,
          venderLTAfterImp: state.selectedTopPNInfo.venderLTAfterImp,
          procLTAfterImp: state.selectedTopPNInfo.procLTAfterImp,
          pphAfterImp: state.selectedTopPNInfo.pphAfterImp,
        };
        const patchTopPNInfoResponse = await API.patchTopPNInfo(state.selectedTopPNInfo.id, newTopPNInfo);

        if (patchTopPNInfoResponse.data.result !== APIResultCode.success) {
          throw new Error(patchTopPNInfoResponse.data.msg);
        }

        ElMessage({
          showClose: true,
          message: "新增成功",
          type: "success",
        });

        state.isShowModifyDialog = false;

        // 刷新
        state.isTableLoading = true;
        await searchTopPNInfo();
      } catch (error) {
        console.log(error);
        ElMessage({
          type: "error",
          message: "處理失敗，請查看詳細訊息",
        });
      } finally {
        state.isModifyDialogLoading = false;
        state.isTableLoading = false;
      }
    };

    /** 按下瀏覽按鈕 */
    const onViewTopPNInfoClicked = async (target: IResTopPNInfoDetailDTO) => {
      try {
        state.isTableLoading = true;

        await GetSelectedItemDetailAsync(target.id);

        setFormStyle();

        state.isShowViewDialog = true;
      } catch (error) {
        console.log(error);
        ElMessage({
          type: "error",
          message: "處理失敗，請查看詳細訊息",
        });
      } finally {
        state.isTableLoading = false;
      }
    };

    //#endregion

    //#region Private Functions
    /** 更新使用者設置偏好 */
    const updateUserPreference = async () => {
      const getPrefResponse = await API.getTableDisplayPreference(tableType);
      if (getPrefResponse.data.result !== 1) {
        console.log(`updateUserPreference : ${getPrefResponse.data.msg}`);
      }
      state.colDisplayCtrl = getPrefResponse.data.content;
    };

    /** 更新所選項目的詳細資料 */
    const GetSelectedItemDetailAsync = async (topPNInfoId: number) => {
      try {
        const TopPNInfoDtlResponse = await API.getTopPNInfoDetailById(topPNInfoId);
        if (TopPNInfoDtlResponse.data.result !== APIResultCode.success) {
          throw new Error(TopPNInfoDtlResponse.data.msg);
        }

        const TopPNInfoDtl = TopPNInfoDtlResponse.data.content;

        state.selectedTopPNInfo.id = TopPNInfoDtl.id;
        state.selectedTopPNInfo.createTime = TopPNInfoDtl.createTime;
        state.selectedTopPNInfo.modifiedTime = TopPNInfoDtl.modifiedTime;
        state.selectedTopPNInfo.topPN = TopPNInfoDtl.topPN;
        state.selectedTopPNInfo.periodId = TopPNInfoDtl.periodId;
        state.selectedTopPNInfo.periodNum = TopPNInfoDtl.periodNum;
        state.selectedTopPNInfo.procLTAfterImp = TopPNInfoDtl.procLTAfterImp;
        state.selectedTopPNInfo.procLTBeforeImp = TopPNInfoDtl.procLTBeforeImp;
        state.selectedTopPNInfo.venderLTAfterImp = TopPNInfoDtl.venderLTAfterImp;
        state.selectedTopPNInfo.venderLTBeforeImp = TopPNInfoDtl.venderLTBeforeImp;
        state.selectedTopPNInfo.pphAfterImp = TopPNInfoDtl.pphAfterImp;
        state.selectedTopPNInfo.pphBeforeImp = TopPNInfoDtl.pphBeforeImp;
        state.selectedTopPNInfo.areaAfterImp = TopPNInfoDtl.areaAfterImp;
        state.selectedTopPNInfo.areaBeforeImp = TopPNInfoDtl.areaBeforeImp;
      } catch (error) {
        console.log(error);
      }
    };

    /** 呼叫API, 取得所有成品料號改善清單 */
    async function searchTopPNInfo() {
      try {
        /* Table Loading Flag 開啟 */
        state.isTableLoading = true;

        const getResult = await API.searchTopPNInfo(state.searchCriteria);
        if (getResult.data.result !== APIResultCode.success) {
          throw new Error(getResult.data.msg);
        }

        //取得成品料號改善清單
        state.displayTopPNInfoList = getResult.data.content.pageItems;

        //更新分頁資料總數
        state.filteredTotal = getResult.data.content.totalItems;
      } catch (error: any) {
        console.error(error);
        ElMessage({
          type: "error",
          message: t("errorMessage"),
        });
      } finally {
        state.isTableLoading = false;
      }
    }

    /** RWD設置 */
    function setFormStyle() {
      // small
      if (window.innerWidth <= 576) {
        state.formLabelPosition = "right";
        state.formItemSize = "default";
        state.formLabelWidth = "auto";
        state.dialogTopVh = "1vh";
        return;
      }

      // medium
      if (window.innerWidth <= 768) {
        state.formLabelPosition = "right";
        state.formItemSize = "default";
        state.formLabelWidth = "180px";
        state.dialogTopVh = "3vh";
        return;
      }

      state.formLabelPosition = "right";
      state.formItemSize = "default";
      state.formLabelWidth = "180px";
      state.dialogTopVh = "8vh";
    }
    //#endregion

    return {
      ...toRefs(state),
      userInfoStore,
      paginatorSetup,
      t,
      modifyTopPNInfoFormRef,
      modifyTopPNInfoFormRules,
      searchCriteriaFormRef,
      searchCriteriaFormRules,
      numeral,

      // function
      onClearSearchTopPNInfoClicked,
      onSearchTopPNInfoClicked,
      onPagesizeChanged,
      onCurrentPageChanged,
      onSortChanged,
      onInputFieldEnterKeyUp,
      onColDisplayCtrlPanelHidded,
      onEditTopPNInfoClicked,
      onSaveModifyResultClicked,
      onViewTopPNInfoClicked,
    };
  },
});
